import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { DOMESTIC_DATA, getOverseasData } from '../birthPlaceData';
import { getDomesticPlaces, getOverseasPlaces, filterPlaces, normalizeSearchQuery } from './placeSearchData';
import { toTraditional } from '../../../utils/langConvert';

const TRAD_LANGS = ['zh-HK', 'zh-TW'];
/** 展示层简→繁转换（仅用于渲染；存值与索引保持简体） */
function makeDisp(lang: string) {
  return (s: string) => (TRAD_LANGS.includes(lang) ? toTraditional(s) : s);
}
import { BirthPlaceSearchBar, BirthPlaceSuggestList } from './BirthPlaceSearch';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface BirthPlacePickerProps {
  value?: string;
  onChange: (value: string) => void;
  onClose: () => void;
  /** 模块专属主题色（hex），不传则使用全局主题变量 */
  accent?: string;
  /** 弹窗标题，不传则使用 i18n 默认标题 */
  title?: string;
  /** 传入出生地（如「北京市 北京市 东城区」）时，显示「与出生地一致」勾选框 */
  syncFrom?: string;
}

const ITEM_HEIGHT = 44;

interface WheelColumnProps {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  flex?: string;
  unknownItems?: string[];
}

function WheelColumn({ items, selectedIndex, onSelect, flex = 'flex-1', unknownItems = [] }: WheelColumnProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const VISIBLE = 5;
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScrollTop = useRef(0);
  const animating = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = selectedIndex * ITEM_HEIGHT;
  }, [selectedIndex, items]);

  const snapToNearest = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    const target = clamped * ITEM_HEIGHT;
    if (el.scrollTop !== target) {
      animating.current = true;
      el.scrollTo({ top: target, behavior: 'smooth' });
      setTimeout(() => { animating.current = false; }, 300);
    }
    if (clamped !== selectedIndex) onSelect(clamped);
  }, [items.length, onSelect, selectedIndex]);

  const handleScroll = useCallback(() => {
    if (isDragging.current || animating.current) return;
    snapToNearest();
  }, [snapToNearest]);

  const onTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    isDragging.current = true;
    startY.current = e.touches[0].clientY;
    startScrollTop.current = containerRef.current?.scrollTop ?? 0;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (!isDragging.current || !containerRef.current) return;
    containerRef.current.scrollTop = startScrollTop.current + (startY.current - e.touches[0].clientY);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    isDragging.current = false;
    snapToNearest();
  };

  const onMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startY.current = e.clientY;
    startScrollTop.current = containerRef.current?.scrollTop ?? 0;
    e.preventDefault();
  };

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    containerRef.current.scrollTop = startScrollTop.current + (startY.current - e.clientY);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    snapToNearest();
  }, [snapToNearest]);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const containerHeight = ITEM_HEIGHT * VISIBLE;
  const paddingItems = Math.floor(VISIBLE / 2);

  return (
    <div className={`${flex} relative overflow-hidden`} style={{ height: containerHeight }}>
      <div
        className="absolute left-1 right-1 pointer-events-none z-10 rounded-xl"
        style={{
          top: paddingItems * ITEM_HEIGHT,
          height: ITEM_HEIGHT,
          background: 'var(--accent-primary-15)',
          border: '1px solid var(--accent-primary-30)',
        }}
      />
      <div
        className="absolute inset-x-0 top-0 pointer-events-none z-20"
        style={{ height: paddingItems * ITEM_HEIGHT, background: 'linear-gradient(to bottom, var(--bg-primary-96), transparent)' }}
      />
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none z-20"
        style={{ height: paddingItems * ITEM_HEIGHT, background: 'linear-gradient(to top, var(--bg-primary-96), transparent)' }}
      />
      <div
        ref={containerRef}
        className="overflow-y-scroll"
        style={{ height: containerHeight, scrollbarWidth: 'none', msOverflowStyle: 'none', overscrollBehavior: 'contain' }}
        onScroll={handleScroll}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
      >
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`top-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
        {items.map((item, idx) => {
          const dist = Math.abs(idx - selectedIndex);
          const isUnknown = unknownItems.includes(item);
          const opacity = dist === 0 ? 1 : dist === 1 ? 0.55 : 0.25;
          const scale = dist === 0 ? 1 : dist === 1 ? 0.88 : 0.78;
          const color = dist === 0
            ? (isUnknown ? 'var(--accent-secondary)' : 'var(--text-primary)')
            : dist === 1 ? 'var(--text-secondary)' : 'var(--text-tertiary)';
          return (
            <div
              key={idx}
              onClick={() => onSelect(idx)}
              className="flex items-center justify-center cursor-pointer select-none px-1"
              style={{
                height: ITEM_HEIGHT,
                opacity,
                transform: `scale(${scale})`,
                transition: 'opacity 0.2s, transform 0.2s',
                color,
                fontWeight: dist === 0 ? 700 : 400,
                fontSize: dist === 0 ? '0.85rem' : '0.75rem',
                fontFamily: "'Noto Serif TC', serif",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item}
            </div>
          );
        })}
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`bot-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
      </div>
    </div>
  );
}

const HK_PROVINCE_IDX = Object.keys(DOMESTIC_DATA).indexOf('香港特别行政区');

function clampIndex(index: number, length: number) {
  return Math.max(0, Math.min(index, Math.max(0, length - 1)));
}

/** 将 hex 颜色（如 "#E8A020"）转为 rgb 字符串 "232,160,32"，用于 rgba() 拼接 */
function hexToRgb(hex: string): string | null {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return null;
  return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
}

/** 将 hex 颜色变暗（factor 0~1，越小越暗），返回新的 hex */
function darkenHex(hex: string, factor: number): string {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return hex;
  return `#${m.slice(1).map(c => Math.round(parseInt(c, 16) * factor).toString(16).padStart(2, '0')).join('')}`;
}

/** 根据 value 字符串解析出初始索引 */
function parsePlaceValue(
  value: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string | string[],
  lang: string,
): {
  mode: 'domestic' | 'overseas';
  provinceIdx: number;
  cityIdx: number;
  districtIdx: number;
  regionIdx: number;
  countryIdx: number;
  overseasAreaIdx: number;
} | null {
  if (!value) return null;
  const parts = value.split(' ');
  if (parts.length < 3) return null;

  const [p, c, d] = parts;

  const unknownProvince = t('birthPlacePicker.unknownPlace') as string;
  const unknownCity = t('birthPlacePicker.beijingTime') as string;
  const unknownDistrict = t('birthPlacePicker.unknownDistrict') as string;
  const unknownRegion = t('birthPlacePicker.unknownPlace') as string;
  const unknownCountry = t('birthPlacePicker.beijingTime') as string;
  const unknownArea = t('birthPlacePicker.unknownArea') as string;

  // ── Try domestic ──
  const allProvinces = Object.keys(DOMESTIC_DATA);
  const provincesList = [...allProvinces, unknownProvince];
  const pIdx = provincesList.indexOf(p);

  if (pIdx >= 0) {
    const isPUnknown = pIdx === provincesList.length - 1;
    const cityMapData = isPUnknown ? {} : (DOMESTIC_DATA[p] ?? {});
    const cityKeys = Object.keys(cityMapData);
    const citiesList = isPUnknown ? [unknownCity] : [...cityKeys, unknownCity];
    const cIdx = citiesList.indexOf(c);

    const isCUnknown = cIdx === citiesList.length - 1;
    const districtKeys = (isPUnknown || isCUnknown) ? [] : (cityMapData[c] ?? []);
    const districtsList = (isPUnknown || isCUnknown)
      ? [unknownDistrict]
      : [...districtKeys, unknownDistrict];
    const dIdx = districtsList.indexOf(d);

    return {
      mode: 'domestic',
      provinceIdx: pIdx >= 0 ? pIdx : 0,
      cityIdx: cIdx >= 0 ? cIdx : 0,
      districtIdx: dIdx >= 0 ? dIdx : 0,
      regionIdx: 0,
      countryIdx: 0,
      overseasAreaIdx: 0,
    };
  }

  // ── Try overseas ──
  const overseasData = getOverseasData(lang);
  const regionsList = [...Object.keys(overseasData), unknownRegion];
  const rIdx = regionsList.indexOf(p);

  if (rIdx >= 0) {
    const isRUnknown = rIdx === regionsList.length - 1;
    const countryMapData = isRUnknown ? {} : (overseasData[p] ?? {});
    const countryKeys = Object.keys(countryMapData);
    const countriesList = isRUnknown ? [unknownCountry] : [...countryKeys, unknownCountry];
    const cIdx = countriesList.indexOf(c);

    const isCUnknown = cIdx === countriesList.length - 1;
    const areaKeys = (isRUnknown || isCUnknown) ? [] : (countryMapData[c] ?? []);
    const areasList = (isRUnknown || isCUnknown)
      ? [unknownArea]
      : [...areaKeys, unknownArea];
    const aIdx = areasList.indexOf(d);

    return {
      mode: 'overseas',
      provinceIdx: HK_PROVINCE_IDX >= 0 ? HK_PROVINCE_IDX : 0,
      cityIdx: 0,
      districtIdx: 0,
      regionIdx: rIdx >= 0 ? rIdx : 0,
      countryIdx: cIdx >= 0 ? cIdx : 0,
      overseasAreaIdx: aIdx >= 0 ? aIdx : 0,
    };
  }

  return null;
}

/** Desktop BirthPlacePicker: centered modal with cascading <select> dropdowns */
function DesktopBirthPlacePicker({ value, onChange, onClose, accent: accentProp, title: titleProp, syncFrom }: BirthPlacePickerProps) {
  const { t, i18n } = useTranslation('profileManage');
  const { isDark } = useTheme();

  const OVERSEAS_DATA = getOverseasData(i18n.language);

  const DOMESTIC_UNKNOWN_PROVINCE = t('birthPlacePicker.unknownPlace') as string;
  const DOMESTIC_UNKNOWN_CITY = t('birthPlacePicker.beijingTime') as string;
  const DOMESTIC_UNKNOWN_DISTRICT = t('birthPlacePicker.unknownDistrict') as string;
  const OVERSEAS_UNKNOWN_REGION = t('birthPlacePicker.unknownPlace') as string;
  const OVERSEAS_UNKNOWN_COUNTRY = t('birthPlacePicker.beijingTime') as string;
  const OVERSEAS_UNKNOWN_AREA = t('birthPlacePicker.unknownArea') as string;

  const init = parsePlaceValue(value, t as (k: string, o?: Record<string, unknown>) => string | string[], i18n.language);
  const [mode, setMode] = useState<'domestic' | 'overseas'>(init?.mode ?? 'domestic');

  // Domestic state
  const PROVINCES_RAW = Object.keys(DOMESTIC_DATA);
  const PROVINCES = [...PROVINCES_RAW, DOMESTIC_UNKNOWN_PROVINCE];
  const initProvIdx = init?.mode === 'domestic' ? (init?.provinceIdx ?? 0) : 0;
  const initCityIdx = init?.mode === 'domestic' ? (init?.cityIdx ?? 0) : 0;
  const initDistIdx = init?.mode === 'domestic' ? (init?.districtIdx ?? 0) : 0;

  const [province, setProvince] = useState(PROVINCES[Math.min(initProvIdx, PROVINCES.length - 1)]);
  const provinceIdx = PROVINCES.indexOf(province);
  const isProvUnknown = province === DOMESTIC_UNKNOWN_PROVINCE;
  const cityMap = isProvUnknown ? {} : (DOMESTIC_DATA[province] ?? {});
  const cityListRaw = Object.keys(cityMap);
  const cityList = isProvUnknown ? [DOMESTIC_UNKNOWN_CITY] : [...cityListRaw, DOMESTIC_UNKNOWN_CITY];
  const [city, setCity] = useState(cityList[Math.min(initCityIdx, cityList.length - 1)]);
  const isCityUnknown = city === DOMESTIC_UNKNOWN_CITY;
  const districtListRaw = (isProvUnknown || isCityUnknown) ? [] : (cityMap[city] ?? []);
  const districtList = (isProvUnknown || isCityUnknown) ? [DOMESTIC_UNKNOWN_DISTRICT] : [...districtListRaw, DOMESTIC_UNKNOWN_DISTRICT];
  const [district, setDistrict] = useState(districtList[Math.min(initDistIdx, districtList.length - 1)]);

  useEffect(() => {
    if (syncGuard.current.province) { syncGuard.current.province = false; return; }
    const newCityList = isProvUnknown ? [DOMESTIC_UNKNOWN_CITY] : [...Object.keys(DOMESTIC_DATA[province] ?? {}), DOMESTIC_UNKNOWN_CITY];
    setCity(newCityList[0] ?? DOMESTIC_UNKNOWN_CITY);
  }, [province]);

  useEffect(() => {
    if (syncGuard.current.city) { syncGuard.current.city = false; return; }
    const newCityMap = isProvUnknown ? {} : (DOMESTIC_DATA[province] ?? {});
    const newDistList = (isProvUnknown || isCityUnknown) ? [DOMESTIC_UNKNOWN_DISTRICT] : [...(newCityMap[city] ?? []), DOMESTIC_UNKNOWN_DISTRICT];
    setDistrict(newDistList[0] ?? DOMESTIC_UNKNOWN_DISTRICT);
  }, [city, province]);

  // Overseas state
  const OVERSEAS_REGIONS_RAW = Object.keys(OVERSEAS_DATA);
  const OVERSEAS_REGIONS = [...OVERSEAS_REGIONS_RAW, OVERSEAS_UNKNOWN_REGION];
  const initRegIdx = init?.mode === 'overseas' ? (init?.regionIdx ?? 0) : 0;
  const initCntIdx = init?.mode === 'overseas' ? (init?.countryIdx ?? 0) : 0;
  const initAreaIdx = init?.mode === 'overseas' ? (init?.overseasAreaIdx ?? 0) : 0;

  const [region, setRegion] = useState(OVERSEAS_REGIONS[Math.min(initRegIdx, OVERSEAS_REGIONS.length - 1)]);
  const isRegUnknown = region === OVERSEAS_UNKNOWN_REGION;
  const countryMap = isRegUnknown ? {} : (OVERSEAS_DATA[region] ?? {});
  const countryListRaw = Object.keys(countryMap);
  const countryList = isRegUnknown ? [OVERSEAS_UNKNOWN_COUNTRY] : [...countryListRaw, OVERSEAS_UNKNOWN_COUNTRY];
  const [country, setCountry] = useState(countryList[Math.min(initCntIdx, countryList.length - 1)]);
  const isCountryUnknown = country === OVERSEAS_UNKNOWN_COUNTRY;
  const areaListRaw = (isRegUnknown || isCountryUnknown) ? [] : (countryMap[country] ?? []);
  const areaList = (isRegUnknown || isCountryUnknown) ? [OVERSEAS_UNKNOWN_AREA] : [...areaListRaw, OVERSEAS_UNKNOWN_AREA];
  const [area, setArea] = useState(areaList[Math.min(initAreaIdx, areaList.length - 1)]);

  useEffect(() => {
    if (syncGuard.current.region) { syncGuard.current.region = false; return; }
    const newCountryList = isRegUnknown ? [OVERSEAS_UNKNOWN_COUNTRY] : [...Object.keys(OVERSEAS_DATA[region] ?? {}), OVERSEAS_UNKNOWN_COUNTRY];
    setCountry(newCountryList[0] ?? OVERSEAS_UNKNOWN_COUNTRY);
  }, [region]);

  useEffect(() => {
    if (syncGuard.current.country) { syncGuard.current.country = false; return; }
    const newCountryMap = isRegUnknown ? {} : (OVERSEAS_DATA[region] ?? {});
    const newAreaList = (isRegUnknown || isCountryUnknown) ? [OVERSEAS_UNKNOWN_AREA] : [...(newCountryMap[country] ?? []), OVERSEAS_UNKNOWN_AREA];
    setArea(newAreaList[0] ?? OVERSEAS_UNKNOWN_AREA);
  }, [country, region]);

  const provinceRef = useRef<HTMLSelectElement>(null);
  const cityRef = useRef<HTMLSelectElement>(null);
  const districtRef = useRef<HTMLSelectElement>(null);
  const regionRef = useRef<HTMLSelectElement>(null);
  const countryRef = useRef<HTMLSelectElement>(null);
  const areaRef = useRef<HTMLSelectElement>(null);
  // 联动「与出生地一致」时跳过级联重置的守卫
  const syncGuard = useRef({ province: false, city: false, region: false, country: false });

  const accentColor = accentProp || 'var(--accent-primary)';
  const accentRgb = accentProp ? hexToRgb(accentProp) : null;
  const modalTitle = titleProp || t('birthPlacePicker.title');
  const disp = makeDisp(i18n.language);

  const [query, setQuery] = useState('');
  const searchPlaces = useMemo(
    () => (mode === 'domestic' ? getDomesticPlaces() : getOverseasPlaces(i18n.language)),
    [mode, i18n.language],
  );
  const searchResults = useMemo(() => filterPlaces(searchPlaces, query), [searchPlaces, query]);
  const searchOptions = useMemo(
    () => searchResults.map((o) => ({ ...o, segments: o.segments.map(disp) })),
    [searchResults, disp],
  );
  const isSearching = query.trim().length > 0;

  const handlePick = (picked: string) => {
    onChange(picked);
    onClose();
  };

  // ── 与出生地一致 ──
  const [sameAsBirth, setSameAsBirth] = useState(false);
  const birthInit = useMemo(
    () => parsePlaceValue(syncFrom, t as (k: string, o?: Record<string, unknown>) => string | string[], i18n.language),
    [syncFrom, i18n.language, t],
  );
  const canSyncFromBirth = Boolean(birthInit);

  const applySyncFromBirth = () => {
    if (!birthInit) return;
    if (birthInit.mode === 'domestic') {
      const pIdx = clampIndex(birthInit.provinceIdx, PROVINCES.length);
      const prov = PROVINCES[pIdx] ?? '';
      const provUnknown = prov === DOMESTIC_UNKNOWN_PROVINCE;
      const cMap = provUnknown ? {} : (DOMESTIC_DATA[prov] ?? {});
      const cList = provUnknown ? [DOMESTIC_UNKNOWN_CITY] : [...Object.keys(cMap), DOMESTIC_UNKNOWN_CITY];
      const cty = cList[clampIndex(birthInit.cityIdx, cList.length)] ?? '';
      const ctyUnknown = cty === DOMESTIC_UNKNOWN_CITY;
      const dList = (provUnknown || ctyUnknown) ? [DOMESTIC_UNKNOWN_DISTRICT] : [...(cMap[cty] ?? []), DOMESTIC_UNKNOWN_DISTRICT];
      const dist = dList[clampIndex(birthInit.districtIdx, dList.length)] ?? '';
      const provChanged = prov !== province;
      const ctyChanged = cty !== city;
      syncGuard.current.province = provChanged;
      syncGuard.current.city = provChanged || ctyChanged;
      setMode('domestic');
      setProvince(prov);
      setCity(cty);
      setDistrict(dist);
    } else {
      const rIdx = clampIndex(birthInit.regionIdx, OVERSEAS_REGIONS.length);
      const reg = OVERSEAS_REGIONS[rIdx] ?? '';
      const regUnknown = reg === OVERSEAS_UNKNOWN_REGION;
      const cMap = regUnknown ? {} : (OVERSEAS_DATA[reg] ?? {});
      const cList = regUnknown ? [OVERSEAS_UNKNOWN_COUNTRY] : [...Object.keys(cMap), OVERSEAS_UNKNOWN_COUNTRY];
      const cty = cList[clampIndex(birthInit.countryIdx, cList.length)] ?? '';
      const ctyUnknown = cty === OVERSEAS_UNKNOWN_COUNTRY;
      const aList = (regUnknown || ctyUnknown) ? [OVERSEAS_UNKNOWN_AREA] : [...(cMap[cty] ?? []), OVERSEAS_UNKNOWN_AREA];
      const area = aList[clampIndex(birthInit.overseasAreaIdx, aList.length)] ?? '';
      const regChanged = reg !== region;
      const ctyChanged = cty !== country;
      syncGuard.current.region = regChanged;
      syncGuard.current.country = regChanged || ctyChanged;
      setMode('overseas');
      setRegion(reg);
      setCountry(cty);
      setArea(area);
    }
  };

  const handleConfirm = () => {
    if (sameAsBirth && syncFrom) {
      onChange(syncFrom);
      onClose();
      return;
    }
    let result = '';
    if (mode === 'domestic') {
      if (isProvUnknown || isCityUnknown) {
        result = `${DOMESTIC_UNKNOWN_PROVINCE} ${DOMESTIC_UNKNOWN_CITY} ${DOMESTIC_UNKNOWN_DISTRICT}`;
      } else if (district === DOMESTIC_UNKNOWN_DISTRICT) {
        result = `${province} ${city} ${DOMESTIC_UNKNOWN_DISTRICT}`;
      } else {
        result = `${province} ${city} ${district}`;
      }
    } else {
      if (isRegUnknown || isCountryUnknown) {
        result = `${OVERSEAS_UNKNOWN_REGION} ${OVERSEAS_UNKNOWN_COUNTRY} ${OVERSEAS_UNKNOWN_AREA}`;
      } else if (area === OVERSEAS_UNKNOWN_AREA) {
        result = `${region} ${country} ${OVERSEAS_UNKNOWN_AREA}`;
      } else {
        result = `${region} ${country} ${area}`;
      }
    }
    onChange(result);
    onClose();
  };

  const selectStyle: React.CSSProperties = {
    background: isDark ? 'rgba(50,30,60,0.95)' : '#fff',
    color: isDark ? '#F0E0E8' : '#1A1A1A',
    border: `1px solid ${isDark ? 'rgba(183,154,197,0.25)' : 'rgba(0,0,0,0.1)'}`,
    borderRadius: 8,
    padding: '7px 28px 7px 10px',
    fontSize: '0.8rem',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    fontFamily: "'Noto Serif TC', serif",
    width: '100%',
  };

  const SelectWrap = ({ label, value: val, onChange: onCh, options: opts, selectRef, nextRef }: { label: string; value: string; onChange: (v: string) => void; options: string[]; selectRef?: React.RefObject<HTMLSelectElement>; nextRef?: React.RefObject<HTMLSelectElement> }) => (
    <div className="flex flex-col gap-1 flex-1">
      <label className="text-[0.6rem]" style={{ color: isDark ? '#8A7B91' : '#888', fontFamily: "'Noto Serif TC', serif" }}>{label}</label>
      <div className="relative">
        <select
          ref={selectRef}
          value={val}
          onChange={e => {
            onCh(e.target.value);
            setTimeout(() => {
              const next = nextRef?.current;
              if (!next) return;
              const sel = next as HTMLSelectElement & { showPicker?: () => void };
              if (typeof sel.showPicker === 'function') {
                try { sel.showPicker(); } catch { next.focus(); }
              } else {
                next.focus();
              }
            }, 80);
          }}
          style={selectStyle}
        >
          {opts.map(o => <option key={o} value={o}>{disp(o)}</option>)}
        </select>
        <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-xs" style={{ color: isDark ? '#8A7B91' : '#888' }} />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" style={{ backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div
        className="relative rounded-2xl border shadow-2xl"
        style={{
          background: isDark ? '#2A1A2F' : '#fff',
          borderColor: isDark ? 'rgba(183,154,197,0.3)' : 'rgba(0,0,0,0.1)',
          width: 480,
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: isDark ? 'rgba(183,154,197,0.15)' : 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full" style={{ background: accentColor }} />
            <h3 className="text-sm font-semibold" style={{ fontFamily: "'Noto Serif TC', serif", color: isDark ? '#F0E0E8' : '#1A1A1A' }}>{modalTitle}</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: isDark ? 'rgba(183,154,197,0.12)' : 'rgba(0,0,0,0.06)' }}>
            <i className="ri-close-line text-sm" style={{ color: isDark ? '#8A7B91' : '#666' }} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="flex items-center gap-3">
            {/* 国内/海外 切换 */}
            <div
              className="flex items-center gap-1 flex-1 min-w-0 rounded-full p-1"
              style={{ background: accentProp ? (isDark ? `rgba(${accentRgb},0.12)` : `rgba(${accentRgb},0.08)`) : (isDark ? 'var(--panel-dark-25)' : 'var(--accent-primary-10)') }}
            >
              {(['domestic', 'overseas'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => { setSameAsBirth(false); setMode(m); setQuery(''); }}
                  className="flex-1 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                  style={{
                    background: mode === m ? (accentProp ? `rgba(${accentRgb},0.22)` : 'var(--accent-primary-25)') : 'transparent',
                    color: mode === m ? (accentProp ? accentColor : (isDark ? 'var(--text-primary)' : 'var(--accent-primary)')) : 'var(--text-secondary)',
                    border: mode === m ? `1px solid ${accentProp ? `rgba(${accentRgb},0.35)` : 'var(--accent-primary-40)'}` : '1px solid transparent',
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {m === 'domestic' ? t('birthPlacePicker.domestic') : t('birthPlacePicker.overseas')}
                </button>
              ))}
            </div>
            {/* 确定 按钮 */}
            <button
              onClick={handleConfirm}
              className="flex-shrink-0 px-6 py-2 rounded-full text-sm font-bold hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{
                background: accentProp
                  ? `linear-gradient(135deg, ${accentColor}, ${darkenHex(accentColor, 0.75)})`
                  : 'linear-gradient(135deg, var(--gradient-btn-from), var(--gradient-btn-to))',
                color: 'var(--btn-text)',
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              {t('birthPlacePicker.confirm')}
            </button>
          </div>
        </div>

        {/* 搜索框 */}
        <div className="px-5 pt-3">
          <BirthPlaceSearchBar
            value={query}
            placeholder={t('birthPlacePicker.searchPlaceholder') as string}
            cancelLabel={t('birthPlacePicker.cancel') as string}
            accentColor={accentColor}
            onChange={setQuery}
            onCancel={() => setQuery('')}
          />
        </div>

        {/* 与出生地一致 */}
        {canSyncFromBirth && !isSearching && (
          <div className="px-5 pt-3">
            <button
              type="button"
              onClick={() => {
                const next = !sameAsBirth;
                setSameAsBirth(next);
                if (next) applySyncFromBirth();
              }}
              className="flex items-center gap-2 select-none cursor-pointer"
            >
              <span
                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors"
                style={{
                  border: `1.5px solid ${sameAsBirth ? accentColor : (isDark ? 'rgba(183,154,197,0.5)' : 'rgba(0,0,0,0.25)')}`,
                  background: sameAsBirth ? accentColor : 'transparent',
                }}
              >
                {sameAsBirth && <i className="ri-check-line text-[0.62rem]" style={{ color: '#fff' }} />}
              </span>
              <span className="text-xs" style={{ fontFamily: "'Noto Serif TC', serif", color: sameAsBirth ? accentColor : (isDark ? '#8A7B91' : '#888') }}>
                {t('birthPlacePicker.sameAsBirth')}
              </span>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {isSearching ? (
            <BirthPlaceSuggestList
              options={searchOptions}
              query={disp(normalizeSearchQuery(query))}
              accentColor={accentColor}
              emptyLabel={t('birthPlacePicker.noResult') as string}
              onPick={handlePick}
            />
          ) : mode === 'domestic' ? (
            <div className="flex gap-3">
              <SelectWrap label={t('birthPlacePicker.province') as string} value={province} onChange={(v) => { setSameAsBirth(false); setProvince(v); }} options={PROVINCES} selectRef={provinceRef} nextRef={cityRef} />
              <SelectWrap label={t('birthPlacePicker.city') as string} value={city} onChange={(v) => { setSameAsBirth(false); setCity(v); }} options={cityList} selectRef={cityRef} nextRef={districtRef} />
              <SelectWrap label={t('birthPlacePicker.district') as string} value={district} onChange={(v) => { setSameAsBirth(false); setDistrict(v); }} options={districtList} selectRef={districtRef} />
            </div>
          ) : (
            <div className="flex gap-3">
              <SelectWrap label={t('birthPlacePicker.region') as string} value={region} onChange={(v) => { setSameAsBirth(false); setRegion(v); }} options={OVERSEAS_REGIONS} selectRef={regionRef} nextRef={countryRef} />
              <SelectWrap label={t('birthPlacePicker.country') as string} value={country} onChange={(v) => { setSameAsBirth(false); setCountry(v); }} options={countryList} selectRef={countryRef} nextRef={areaRef} />
              <SelectWrap label={t('birthPlacePicker.area') as string} value={area} onChange={(v) => { setSameAsBirth(false); setArea(v); }} options={areaList} selectRef={areaRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BirthPlacePicker(props: BirthPlacePickerProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  if (isDesktop) return <DesktopBirthPlacePicker {...props} />;
  return <MobileBirthPlacePicker {...props} />;
}

function MobileBirthPlacePicker({ value, onChange, onClose, accent: accentProp, title: titleProp, syncFrom }: BirthPlacePickerProps) {
  const { t, i18n } = useTranslation('profileManage');
  const { isDark } = useTheme();

  const init = parsePlaceValue(value, t, i18n.language);

  const [mode, setMode] = useState<'domestic' | 'overseas'>(init?.mode ?? 'domestic');
  const [provinceIdx, setProvinceIdx] = useState(init?.provinceIdx ?? (HK_PROVINCE_IDX >= 0 ? HK_PROVINCE_IDX : 0));
  const [cityIdx, setCityIdx] = useState(init?.cityIdx ?? 0);
  const [districtIdx, setDistrictIdx] = useState(init?.districtIdx ?? 0);
  const [regionIdx, setRegionIdx] = useState(init?.regionIdx ?? 0);
  const [countryIdx, setCountryIdx] = useState(init?.countryIdx ?? 0);
  const [overseasAreaIdx, setOverseasAreaIdx] = useState(init?.overseasAreaIdx ?? 0);
  // 联动「与出生地一致」时跳过级联重置的守卫
  const syncGuard = useRef({ province: false, city: false, region: false, country: false, mode: false });

  const OVERSEAS_DATA = getOverseasData(i18n.language);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const DOMESTIC_UNKNOWN_PROVINCE = t('birthPlacePicker.unknownPlace');
  const DOMESTIC_UNKNOWN_CITY = t('birthPlacePicker.beijingTime');
  const DOMESTIC_UNKNOWN_DISTRICT = t('birthPlacePicker.unknownDistrict');
  const OVERSEAS_UNKNOWN_REGION = t('birthPlacePicker.unknownPlace');
  const OVERSEAS_UNKNOWN_COUNTRY = t('birthPlacePicker.beijingTime');
  const OVERSEAS_UNKNOWN_AREA = t('birthPlacePicker.unknownArea');

  const PROVINCES_RAW = Object.keys(DOMESTIC_DATA);
  const PROVINCES = [...PROVINCES_RAW, DOMESTIC_UNKNOWN_PROVINCE];
  const safeProvinceIdx = clampIndex(provinceIdx, PROVINCES.length);

  const currentProvince = PROVINCES[safeProvinceIdx] ?? '';
  const isProvinceUnknown = safeProvinceIdx === PROVINCES.length - 1;

  const cityMap = isProvinceUnknown ? {} : (DOMESTIC_DATA[currentProvince] ?? {});
  const cityListRaw = Object.keys(cityMap);
  const cityList = isProvinceUnknown ? [DOMESTIC_UNKNOWN_CITY] : [...cityListRaw, DOMESTIC_UNKNOWN_CITY];
  const safeCityIdx = clampIndex(cityIdx, cityList.length);

  const isCityUnknown = safeCityIdx === cityList.length - 1;
  const currentCity = cityList[safeCityIdx] ?? '';

  const districtListRaw = (isProvinceUnknown || isCityUnknown) ? [] : (cityMap[currentCity] ?? []);
  const districtList = (isProvinceUnknown || isCityUnknown)
    ? [DOMESTIC_UNKNOWN_DISTRICT]
    : [...districtListRaw, DOMESTIC_UNKNOWN_DISTRICT];
  const safeDistrictIdx = clampIndex(districtIdx, districtList.length);

  const OVERSEAS_REGIONS_RAW = Object.keys(OVERSEAS_DATA);
  const OVERSEAS_REGIONS = [...OVERSEAS_REGIONS_RAW, OVERSEAS_UNKNOWN_REGION];
  const safeRegionIdx = clampIndex(regionIdx, OVERSEAS_REGIONS.length);

  const currentRegion = OVERSEAS_REGIONS[safeRegionIdx] ?? '';
  const isRegionUnknown = safeRegionIdx === OVERSEAS_REGIONS.length - 1;

  const countryMap = isRegionUnknown ? {} : (OVERSEAS_DATA[currentRegion] ?? {});
  const countryListRaw = Object.keys(countryMap);
  const countryList = isRegionUnknown ? [OVERSEAS_UNKNOWN_COUNTRY] : [...countryListRaw, OVERSEAS_UNKNOWN_COUNTRY];
  const safeCountryIdx = clampIndex(countryIdx, countryList.length);

  const isCountryUnknown = safeCountryIdx === countryList.length - 1;
  const currentCountry = countryList[safeCountryIdx] ?? '';

  const areaListRaw = (isRegionUnknown || isCountryUnknown) ? [] : (countryMap[currentCountry] ?? []);
  const areaList = (isRegionUnknown || isCountryUnknown)
    ? [OVERSEAS_UNKNOWN_AREA]
    : [...areaListRaw, OVERSEAS_UNKNOWN_AREA];
  const safeOverseasAreaIdx = clampIndex(overseasAreaIdx, areaList.length);

  useEffect(() => {
    if (syncGuard.current.province) { syncGuard.current.province = false; return; }
    setCityIdx(0); setDistrictIdx(0);
  }, [provinceIdx]);
  useEffect(() => {
    if (syncGuard.current.city) { syncGuard.current.city = false; return; }
    setDistrictIdx(0);
  }, [cityIdx]);
  useEffect(() => {
    if (syncGuard.current.region) { syncGuard.current.region = false; return; }
    setCountryIdx(0); setOverseasAreaIdx(0);
  }, [regionIdx]);
  useEffect(() => {
    if (syncGuard.current.country) { syncGuard.current.country = false; return; }
    setOverseasAreaIdx(0);
  }, [countryIdx]);

  useEffect(() => {
    if (mode === 'overseas') {
      if (syncGuard.current.mode) { syncGuard.current.mode = false; return; }
      setRegionIdx(0);
      setCountryIdx(0);
      setOverseasAreaIdx(0);
    }
  }, [mode]);

  // ── 与出生地一致 ──
  const [sameAsBirth, setSameAsBirth] = useState(false);
  const birthInit = useMemo(() => parsePlaceValue(syncFrom, t, i18n.language), [syncFrom, i18n.language, t]);
  const canSyncFromBirth = Boolean(birthInit);

  const applySyncFromBirth = () => {
    if (!birthInit) return;
    if (birthInit.mode === 'domestic') {
      const pChanged = birthInit.provinceIdx !== provinceIdx;
      const cChanged = birthInit.cityIdx !== cityIdx;
      syncGuard.current.province = pChanged;
      syncGuard.current.city = pChanged || cChanged;
      setMode('domestic');
      setProvinceIdx(birthInit.provinceIdx);
      setCityIdx(birthInit.cityIdx);
      setDistrictIdx(birthInit.districtIdx);
    } else {
      syncGuard.current.mode = mode !== 'overseas';
      syncGuard.current.region = birthInit.regionIdx !== regionIdx;
      syncGuard.current.country = birthInit.countryIdx !== countryIdx;
      setMode('overseas');
      setRegionIdx(birthInit.regionIdx);
      setCountryIdx(birthInit.countryIdx);
      setOverseasAreaIdx(birthInit.overseasAreaIdx);
    }
  };

  const handleConfirm = () => {
    if (sameAsBirth && syncFrom) {
      onChange(syncFrom);
      onClose();
      return;
    }
    let result = '';

    if (mode === 'domestic') {
      const district = districtList[safeDistrictIdx] ?? '';
      if (isProvinceUnknown || isCityUnknown) {
        result = `${DOMESTIC_UNKNOWN_PROVINCE} ${DOMESTIC_UNKNOWN_CITY} ${DOMESTIC_UNKNOWN_DISTRICT}`;
      } else if (safeDistrictIdx === districtList.length - 1) {
        result = `${currentProvince} ${currentCity} ${DOMESTIC_UNKNOWN_DISTRICT}`;
      } else {
        result = `${currentProvince} ${currentCity} ${district}`;
      }
    } else {
      const area = areaList[safeOverseasAreaIdx] ?? '';
      if (isRegionUnknown || isCountryUnknown) {
        result = `${OVERSEAS_UNKNOWN_REGION} ${OVERSEAS_UNKNOWN_COUNTRY} ${OVERSEAS_UNKNOWN_AREA}`;
      } else if (safeOverseasAreaIdx === areaList.length - 1) {
        result = `${currentRegion} ${currentCountry} ${OVERSEAS_UNKNOWN_AREA}`;
      } else {
        result = `${currentRegion} ${currentCountry} ${area}`;
      }
    }

    onChange(result);
    onClose();
  };

  const accentColor = accentProp || 'var(--accent-primary)';
  // 辅助色：从 accent 推导淡/深变体（用于不透明度叠加和渐变）
  const accentRgb = accentProp ? hexToRgb(accentProp) : null;
  const modalTitle = titleProp || t('birthPlacePicker.title');
  const disp = makeDisp(i18n.language);

  const [query, setQuery] = useState('');
  const searchPlaces = useMemo(
    () => (mode === 'domestic' ? getDomesticPlaces() : getOverseasPlaces(i18n.language)),
    [mode, i18n.language],
  );
  const searchResults = useMemo(() => filterPlaces(searchPlaces, query), [searchPlaces, query]);
  const searchOptions = useMemo(
    () => searchResults.map((o) => ({ ...o, segments: o.segments.map(disp) })),
    [searchResults, disp],
  );
  const isSearching = query.trim().length > 0;

  const handlePick = (picked: string) => {
    onChange(picked);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-end" style={{ maxWidth: 375, margin: '0 auto' }}>
      <div className="absolute inset-0 bg-black/60" style={{ backdropFilter: 'blur(6px)' }} onClick={onClose} />
      <div
        className="relative w-full rounded-t-3xl shadow-2xl border-t overflow-hidden"
        style={{
          background: 'var(--bg-modal)',
          maxHeight: '85dvh',
          display: 'flex',
          flexDirection: 'column',
          borderColor: 'var(--border-color)',
        }}
      >
        <div
          className="w-10 h-1 rounded-full mx-auto mt-4 mb-0"
          style={{ background: 'var(--text-primary-30)' }}
        />

        <div className="relative flex items-center justify-center px-5 py-4">
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-4 rounded-full"
              style={{
                background: accentColor,
                boxShadow: accentProp
                  ? `0 0 6px ${accentColor}66`
                  : (isDark ? 'rgba(183,154,197,0.5)' : 'rgba(232,160,32,0.4)'),
              }}
            />
            <h3
              className="text-base font-semibold"
              style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-primary)' }}
            >
              {modalTitle}
            </h3>
          </div>
          <button onClick={onClose} className="absolute right-4 -top-1 w-8 h-8 flex items-center justify-center rounded-full transition-colors">
            <i className="ri-close-line text-lg" style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>

        <div className="px-4 pb-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-1 flex-1 min-w-0 rounded-full p-1"
              style={{
                background: accentProp
                  ? (isDark ? `rgba(${accentRgb},0.12)` : `rgba(${accentRgb},0.08)`)
                  : (isDark ? 'var(--panel-dark-25)' : 'var(--accent-primary-10)'),
              }}
            >
              {(['domestic', 'overseas'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => { setSameAsBirth(false); setMode(m); setQuery(''); }}
                  className="flex-1 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                  style={{
                    background: mode === m
                      ? (accentProp ? `rgba(${accentRgb},0.22)` : 'var(--accent-primary-25)')
                      : 'transparent',
                    color: mode === m
                      ? (accentProp ? accentColor : (isDark ? 'var(--text-primary)' : 'var(--accent-primary)'))
                      : 'var(--text-secondary)',
                    border: mode === m
                      ? `1px solid ${accentProp ? `rgba(${accentRgb},0.35)` : 'var(--accent-primary-40)'}`
                      : '1px solid transparent',
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {m === 'domestic' ? t('birthPlacePicker.domestic') : t('birthPlacePicker.overseas')}
                </button>
              ))}
            </div>
            <button
              onClick={handleConfirm}
              className="flex-shrink-0 px-5 py-2 rounded-full text-sm font-bold active:scale-[0.98] transition-transform whitespace-nowrap"
              style={{
                background: accentProp
                  ? `linear-gradient(135deg, ${accentColor}, ${darkenHex(accentColor, 0.75)})`
                  : 'linear-gradient(135deg, var(--gradient-btn-from), var(--gradient-btn-to))',
                color: 'var(--btn-text)',
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              {t('birthPlacePicker.confirm')}
            </button>
          </div>
        </div>

        {/* 搜索框 */}
        <div className="px-4 pb-3">
          <BirthPlaceSearchBar
            value={query}
            placeholder={t('birthPlacePicker.searchPlaceholder') as string}
            cancelLabel={t('birthPlacePicker.cancel') as string}
            accentColor={accentColor}
            onChange={setQuery}
            onCancel={() => setQuery('')}
          />
        </div>

        {/* 与出生地一致 */}
        {canSyncFromBirth && !isSearching && (
          <div className="px-4 pb-3">
            <button
              type="button"
              onClick={() => {
                const next = !sameAsBirth;
                setSameAsBirth(next);
                if (next) applySyncFromBirth();
              }}
              className="flex items-center gap-2 select-none cursor-pointer"
            >
              <span
                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors"
                style={{
                  border: `1.5px solid ${sameAsBirth ? accentColor : 'var(--text-tertiary)'}`,
                  background: sameAsBirth ? accentColor : 'transparent',
                }}
              >
                {sameAsBirth && <i className="ri-check-line text-[0.62rem]" style={{ color: '#fff' }} />}
              </span>
              <span className="text-xs" style={{ fontFamily: "'Noto Serif TC', serif", color: sameAsBirth ? accentColor : 'var(--text-secondary)' }}>
                {t('birthPlacePicker.sameAsBirth')}
              </span>
            </button>
          </div>
        )}

        {isSearching ? (
          <div className="px-2 pb-6 overflow-y-auto" style={{ maxHeight: '52vh' }}>
            <BirthPlaceSuggestList
              options={searchOptions}
              query={disp(normalizeSearchQuery(query))}
              accentColor={accentColor}
              emptyLabel={t('birthPlacePicker.noResult') as string}
              onPick={handlePick}
            />
          </div>
        ) : (
          <>
        <div className="flex px-3 pb-1">
          {mode === 'domestic' ? (
            <>
              <div className="flex-1 text-center text-[0.62rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthPlacePicker.province')}</div>
              <div className="flex-1 text-center text-[0.62rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthPlacePicker.city')}</div>
              <div className="flex-1 text-center text-[0.62rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthPlacePicker.district')}</div>
            </>
          ) : (
            <>
              <div className="flex-1 text-center text-[0.62rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthPlacePicker.region')}</div>
              <div className="flex-1 text-center text-[0.62rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthPlacePicker.country')}</div>
              <div className="flex-1 text-center text-[0.62rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthPlacePicker.area')}</div>
            </>
          )}
        </div>

        <div className="mx-4 h-px" style={{ background: accentProp ? `rgba(${accentRgb},0.15)` : 'var(--accent-primary-15)' }} />

        <div className="flex px-3 py-1">
          {mode === 'domestic' ? (
            <>
              <WheelColumn items={PROVINCES.map(disp)} selectedIndex={safeProvinceIdx} onSelect={(i) => { setSameAsBirth(false); setProvinceIdx(i); }} unknownItems={[disp(DOMESTIC_UNKNOWN_PROVINCE)]} />
              <WheelColumn items={cityList.map(disp)} selectedIndex={safeCityIdx} onSelect={(i) => { setSameAsBirth(false); setCityIdx(i); }} unknownItems={[disp(DOMESTIC_UNKNOWN_CITY)]} />
              <WheelColumn items={districtList.map(disp)} selectedIndex={safeDistrictIdx} onSelect={(i) => { setSameAsBirth(false); setDistrictIdx(i); }} unknownItems={[disp(DOMESTIC_UNKNOWN_DISTRICT)]} />
            </>
          ) : (
            <>
              <WheelColumn items={OVERSEAS_REGIONS.map(disp)} selectedIndex={safeRegionIdx} onSelect={(i) => { setSameAsBirth(false); setRegionIdx(i); }} unknownItems={[disp(OVERSEAS_UNKNOWN_REGION)]} />
              <WheelColumn items={countryList.map(disp)} selectedIndex={safeCountryIdx} onSelect={(i) => { setSameAsBirth(false); setCountryIdx(i); }} unknownItems={[disp(OVERSEAS_UNKNOWN_COUNTRY)]} />
              <WheelColumn items={areaList.map(disp)} selectedIndex={safeOverseasAreaIdx} onSelect={(i) => { setSameAsBirth(false); setOverseasAreaIdx(i); }} unknownItems={[disp(OVERSEAS_UNKNOWN_AREA)]} />
            </>
          )}
        </div>

          <div className="pb-5" />
          </>
        )}
      </div>
    </div>
  );
}
